import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { formatoMonto } from '../compartidos/useAnalisis';

const VISTAS = [['todos', 'Todos'], ['cvu', 'Por CVU'], ['sponsor', 'Por banco sponsor']];
const fecha = (f) => (f ? new Date(f).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '—');

const Filtros = ({ rango, setRango, filtros, setFiltros }) => (
  <div className="flex flex-wrap items-end gap-3" data-testid="movs-filtros">
    {[['desde', 'Desde'], ['hasta', 'Hasta']].map(([k, l]) => (
      <label key={k} className="text-xs text-slate-500">{l}
        <Input type="date" value={rango[k]} onChange={(e) => setRango({ ...rango, [k]: e.target.value })} className="mt-1 h-9 w-40" data-testid={`movs-${k}`} />
      </label>
    ))}
    <label className="text-xs text-slate-500">Sentido
      <select value={filtros.sentido} onChange={(e) => setFiltros({ ...filtros, sentido: e.target.value })}
        className="mt-1 block h-9 rounded-md border border-slate-300 px-2 text-sm" data-testid="movs-sentido">
        <option value="">Todos</option><option value="INGRESO">Ingresos</option><option value="EGRESO">Egresos</option>
      </select>
    </label>
    <label className="text-xs text-slate-500">Monto mínimo
      <Input type="number" value={filtros.monto_min} onChange={(e) => setFiltros({ ...filtros, monto_min: e.target.value })} className="mt-1 h-9 w-32" data-testid="movs-monto-min" />
    </label>
    <label className="text-xs text-slate-500">CVU
      <Input value={filtros.cvu} onChange={(e) => setFiltros({ ...filtros, cvu: e.target.value.replace(/\D/g, '') })} placeholder="22 dígitos" className="mt-1 h-9 w-56 font-mono" data-testid="movs-cvu" />
    </label>
  </div>
);

const TablaTodos = ({ data }) => (
  <table className="w-full text-sm" data-testid="movs-tabla-todos">
    <thead><tr className="border-b text-left text-[11px] uppercase text-slate-400">
      <th className="py-2">Fecha</th><th>Sentido</th><th className="text-right">Monto</th><th className="pl-4">Contraparte</th><th>CVU propio</th><th>Sponsor</th>
    </tr></thead>
    <tbody className="divide-y divide-slate-100">
      {data.movimientos.map((m) => (
        <tr key={m.id_waya} className="hover:bg-slate-50">
          <td className="py-1.5 font-mono text-xs text-slate-500">{fecha(m.fecha)}</td>
          <td><span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${m.sentido === 'INGRESO' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>{m.sentido}</span></td>
          <td className="text-right font-mono text-xs font-semibold">{formatoMonto(m.monto)}</td>
          <td className="pl-4"><p className="truncate text-xs font-medium text-slate-800">{m.contraparte_nombre || '—'}</p><p className="font-mono text-[10px] text-slate-400">{m.contraparte_cuit || m.contraparte_cvu}</p></td>
          <td className="font-mono text-[11px] text-slate-500">{m.cvu_propio}</td>
          <td className="text-xs text-slate-600">{m.sponsor}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const TablaGrupos = ({ data, vista, onCvu }) => (
  <table className="w-full text-sm" data-testid={`movs-tabla-${vista}`}>
    <thead><tr className="border-b text-left text-[11px] uppercase text-slate-400">
      <th className="py-2">{vista === 'cvu' ? 'CVU' : 'Banco sponsor'}</th>{vista === 'cvu' && <th>Sponsor</th>}
      <th className="text-right">Cantidad</th><th className="text-right">Ingresos</th><th className="text-right">Egresos</th><th className="pl-4">Primer mov.</th><th>Último mov.</th>
    </tr></thead>
    <tbody className="divide-y divide-slate-100">
      {data.grupos.map((g) => (
        <tr key={g.cvu || g.sponsor} onClick={() => vista === 'cvu' && onCvu(g.cvu)} data-testid={`movs-grupo-${g.cvu || g.sponsor}`}
          className={vista === 'cvu' ? 'cursor-pointer hover:bg-sky-50' : ''}>
          <td className="py-1.5 font-mono text-xs">{vista === 'cvu' ? g.cvu : g.sponsor}</td>{vista === 'cvu' && <td className="text-xs">{g.sponsor}</td>}
          <td className="text-right font-mono text-xs">{g.cantidad}</td>
          <td className="text-right font-mono text-xs text-emerald-700">{formatoMonto(g.ingresos)}</td>
          <td className="text-right font-mono text-xs text-rose-700">{formatoMonto(g.egresos)}</td>
          <td className="pl-4 font-mono text-[11px] text-slate-500">{fecha(g.primer)}</td><td className="font-mono text-[11px] text-slate-500">{fecha(g.ultimo)}</td>
        </tr>
      ))}
      <tr className="bg-slate-50 font-semibold" data-testid="movs-grupos-total">
        <td className="py-1.5 text-xs" colSpan={vista === 'cvu' ? 2 : 1}>Total</td>
        <td className="text-right font-mono text-xs">{data.total_cantidad}</td>
        <td className="text-right font-mono text-xs">{formatoMonto(data.total_ingresos)}</td>
        <td className="text-right font-mono text-xs">{formatoMonto(data.total_egresos)}</td><td colSpan={2} />
      </tr>
    </tbody>
  </table>
);

// C: pestaña Movimientos de la ficha. El rango se comparte con la pestaña Análisis.
export const ClienteMovimientos = ({ cuit, rango, setRango }) => {
  const [vista, setVista] = useState('todos');
  const [filtros, setFiltros] = useState({ sentido: '', monto_min: '', cvu: '' });
  const [aplicados, setAplicados] = useState(filtros);
  const [pagina, setPagina] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAplicados(filtros), 500);
    return () => clearTimeout(t);
  }, [filtros]);

  const params = useCallback(() => ({
    desde: rango.desde, hasta: rango.hasta, sentido: aplicados.sentido || undefined,
    monto_min: aplicados.monto_min || undefined, cvu: aplicados.cvu.length === 22 ? aplicados.cvu : undefined,
  }), [rango, aplicados]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const url = vista === 'todos' ? `/padron-interno/cliente/${cuit}/movimientos` : `/padron-interno/cliente/${cuit}/movimientos/resumen`;
      const p = vista === 'todos' ? { ...params(), pagina } : { ...params(), agrupar: vista };
      const r = await api.get(url, { params: p, timeout: 120000 });
      setData({ vista, ...r.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudieron cargar los movimientos');
      setData(null);
    } finally { setLoading(false); }
  }, [cuit, vista, pagina, params]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { setPagina(1); }, [vista, rango, aplicados]);

  const exportar = async () => {
    setExportando(true);
    try {
      const r = await api.get(`/padron-interno/cliente/${cuit}/movimientos/exportar`, { params: { ...params(), vista }, responseType: 'blob', timeout: 300000 });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `movimientos_${cuit}_${vista}_${rango.desde}_${rango.hasta}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('No se pudo exportar'); } finally { setExportando(false); }
  };

  const verCvu = (cvu) => { const f = { ...filtros, cvu }; setFiltros(f); setAplicados(f); setVista('todos'); };

  return (
    <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="cliente-movimientos">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid="movs-vista">
          {VISTAS.map(([v, l]) => (
            <button key={v} type="button" onClick={() => setVista(v)} data-testid={`movs-vista-${v}`}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${vista === v ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>{l}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={cargar} disabled={loading} data-testid="movs-recargar"><RefreshCw size={14} className={loading ? 'animate-spin' : ''} /></Button>
          <Button variant="outline" size="sm" onClick={exportar} disabled={exportando} data-testid="movs-exportar">
            {exportando ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Download size={14} className="mr-1" />} Exportar a Excel
          </Button>
        </div>
      </div>
      <Filtros rango={rango} setRango={setRango} filtros={filtros} setFiltros={setFiltros} />
      {data?.avisos?.map((a) => <p key={a} className="text-xs text-amber-700">{a}</p>)}
      {loading && !data ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-400" /></div> : data && data.vista === vista && (
        <div className={`overflow-x-auto ${loading ? 'opacity-50' : ''}`}>
          {vista === 'todos' ? <TablaTodos data={data} /> : <TablaGrupos data={data} vista={vista} onCvu={verCvu} />}
          {vista === 'todos' && (
            <div className="mt-3 flex items-center justify-between text-xs text-slate-500" data-testid="movs-paginacion">
              <span data-testid="movs-total">{data.total} movimientos · página {data.pagina} de {Math.max(1, data.paginas)}</span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)} data-testid="movs-anterior"><ChevronLeft size={14} /></Button>
                <Button variant="outline" size="sm" disabled={pagina >= data.paginas} onClick={() => setPagina(pagina + 1)} data-testid="movs-siguiente"><ChevronRight size={14} /></Button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default ClienteMovimientos;
